/**
 * Graphics quality. High is the full look; Low trades looks for frame time
 * (and so main-thread time for a host's sim): device pixel ratio 1, no
 * antialiasing, a 30 fps cap, half the fog puffs and thinned tile props. Auto
 * starts at High and drops to Low for the rest of the match when the frame
 * rate stays low (`AutoQualityMonitor`).
 */

export type QualitySetting = 'high' | 'low' | 'auto';

export const QUALITY_SETTINGS: readonly QualitySetting[] = ['high', 'low', 'auto'];

/** Highest device pixel ratio drawn at High: 2× and 3× screens cost 1.8–4× the pixels for little. */
export const MAX_DPR = 1.5;
/** Frame-rate cap at Low. */
export const LOW_FPS = 30;

export interface QualityProfile {
  low: boolean;
  /** For the Canvas `dpr` prop: a [min, max] clamp of the device's ratio, or a fixed one. */
  dpr: number | [number, number];
  /** Fixed per WebGL context: a change (Auto's switch to Low too) remounts the Canvas. */
  antialias: boolean;
  /** Frames per second to cap at, or null for the display's rate. */
  fpsCap: number | null;
  fogPuffsPerTile: number;
  /** Fraction of each tile's props (trees, wheat) drawn. */
  decorationDensity: number;
}

export function isQualitySetting(value: unknown): value is QualitySetting {
  return QUALITY_SETTINGS.includes(value as QualitySetting);
}

/** What `setting` draws; `autoLow`: Auto has switched to Low. */
export function qualityProfile(setting: QualitySetting, autoLow: boolean): QualityProfile {
  const low = setting === 'low' || (setting === 'auto' && autoLow);
  return {
    low,
    dpr: low ? 1 : [1, MAX_DPR],
    antialias: !low,
    fpsCap: low ? LOW_FPS : null,
    fogPuffsPerTile: low ? 2 : 4,
    decorationDensity: low ? 0.5 : 1,
  };
}
